import { dayGap, spokenDate, weekdayName } from '../calendar'
import type { Reckoning } from '../calendar'

/**
 * The status ribbon under the bar: where she stands in the month, and what is coming.
 *
 * Everything on it is read off the published calendar. There is no sunset time because the
 * calendar does not carry one, and a time computed here would make this site a second author of
 * a fact Torahly owns.
 */
export function Ribbon({
  reckoning,
  name,
  memberLine,
}: {
  reckoning: Reckoning | null
  name: string | null
  memberLine?: string | null
}) {
  const today = reckoning?.today ?? null
  const nextFeast = reckoning?.upcomingFeasts[0] ?? null
  const feastGap = today && nextFeast ? dayGap(today.d, nextFeast.d) : null

  return (
    <section className="ribbon" aria-label="Today">
      <div className="wrap">
        <div className="ribbon-inner">
          <div className="ribbon-greet">
            <span className="ribbon-name">{name ? `Shalom, ${name}` : 'Shalom'}</span>
            {today ? (
              <>
                <span className="ribbon-sep">·</span>
                <span className="ribbon-date">{spokenDate(today)}</span>
                <span className="ribbon-week">
                  ({weekdayName(today)}
                  {today.f ? `, ${today.f}` : ' of the week'})
                </span>
              </>
            ) : (
              <span className="ribbon-week">Couldn't reach the calendar.</span>
            )}
          </div>

          <div className="ribbon-pills">
            {reckoning && reckoning.daysToSabbath !== null && (
              <span className="pill pill-gold">
                <i className="ph-fill ph-sparkle" aria-hidden="true" />
                <span>{sabbathWords(reckoning.daysToSabbath)}</span>
              </span>
            )}
            {nextFeast && feastGap !== null && (
              <span className="pill pill-rose">
                <i className="ph-bold ph-bell-simple-ringing" aria-hidden="true" />
                <span>
                  {nextFeast.f} <strong>{whenWords(feastGap)}</strong>
                </span>
              </span>
            )}
            {memberLine && (
              <span className="pill pill-plain">
                <i className="ph-fill ph-seal-check" aria-hidden="true" />
                <span>{memberLine}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export function whenWords(gap: number): string {
  if (gap === 0) return 'today'
  if (gap === 1) return 'tomorrow'
  return `in ${gap} days`
}

function sabbathWords(days: number): React.ReactNode {
  if (days === 0) return <>The Sabbath is <strong>today</strong></>
  if (days === 1) return <>Sabbath <strong>tomorrow</strong></>
  return (
    <>
      Sabbath in <strong>{days} days</strong>
    </>
  )
}
